import { defineStore } from 'pinia'
import { computed, ComputedRef, ref, watch } from 'vue'

import { getWebsite, getWebsites } from '@/api'
import { useSavedFilters } from '@/composables'
import { CLINIC_DEFAULT_FILTERS, CLINIC_FILTERS_KEY } from '@/constants'
import { ClinicType, SelectItemType } from '@/types'

export const useClinicStore = defineStore('clinics', () => {
  const { filters } = useSavedFilters(CLINIC_DEFAULT_FILTERS, CLINIC_FILTERS_KEY)
  const currentClinic = ref<ClinicType | undefined>(filters.value.clinic)
  const currentClinicId = ref<number>(filters.value.clinic?.id || 0)

  const { responseData: clinics, execute: fetchClinics, isFetching, isFinished, error: clinicsError } = getWebsites()

  if (!clinics.value && !isFetching.value) {
    fetchClinics()
  }

  watch(isFinished, val => {
    if (val) {
      if (clinics.value?.length && !clinicsError.value) {
        const clinicIndex = clinics.value.findIndex(c => c.id === currentClinicId.value)

        currentClinicId.value = clinicIndex >= 0 ? clinics.value[clinicIndex].id : clinics.value[0].id
      } else {
        setClinic(undefined)
      }
    }
  })

  const {
    responseData: clinic,
    execute: fetchClinic,
    isFetching: isFetchingClinic,
    isFinished: isFinishedClinic,
    error: clinicError,
  } = getWebsite(currentClinicId)

  watch(
    currentClinicId,
    val => {
      if (val) fetchClinic()
    },
    { immediate: true }
  )

  watch(isFinishedClinic, val => {
    if (val) {
      if (!clinicError.value && clinic.value) {
        setClinic(clinic.value)
      } else if (currentClinic.value?.id && currentClinic.value?.id !== currentClinicId.value) {
        currentClinicId.value = currentClinic.value.id
      }
    }
  })

  const clinicsOptions: ComputedRef<SelectItemType[]> = computed(
    () =>
      clinics.value?.map(c => ({
        id: String(c.id),
        name: c.name,
      })) || []
  )

  const permissions = computed(() => currentClinic.value?.invite?.permissions)
  const currentRole = computed(() => currentClinic.value?.invite?.role)
  const isSuperUser = computed(() => currentClinic.value?.invite?.is_superuser)

  function getClinicById(id: number) {
    return clinics.value?.find(clinic => clinic.id === id)
  }

  function setClinicById(id: number | string) {
    currentClinicId.value = Number(id)
  }

  function updateClinicInList(updatedClinic: ClinicType) {
    const index = clinics.value?.findIndex(c => c.id === updatedClinic.id)

    if (index >= 0) {
      clinics.value[index] = updatedClinic

      if (currentClinicId.value === updatedClinic.id) {
        fetchClinic()
      }
    }
  }

  function setClinic(clinic?: ClinicType) {
    currentClinic.value = clinic
    filters.value.clinic = clinic

    if (clinic) {
      if (currentClinicId.value !== clinic.id) {
        currentClinicId.value = clinic.id
      }
    } else {
      currentClinicId.value = 0
    }
  }

  return {
    currentClinicId,
    currentClinic,
    clinics,
    clinicsOptions,
    isFetching,
    isFinished,
    clinicsError,
    permissions,
    currentRole,
    isSuperUser,
    isFetchingClinic,

    fetchClinics,
    getClinicById,
    setClinicById,
    updateClinicInList,
  }
})
